/**
 * 첨부 다운로드 공통 경로 — 화면 세 곳(메일 상세 / 첨부 라이브러리 / 새 창)이 같은 규칙을 쓰도록.
 *
 * <h3>왜 &lt;a href&gt; 직링크가 아니라 XHR + blob 인가</h3>
 * 다운로드 endpoint 는 X-TOKEN 헤더 인증이라 브라우저가 스스로 여는 요청(새 탭·네이티브 다운로드)에는
 * 헤더를 실을 수 없다. ?token= 쿼리를 붙이면 네이티브 다운로드가 되지만 <b>토큰이 URL 에 남는다</b> —
 * nginx 액세스 로그·브라우저 다운로드 기록에 그대로 찍히므로 첨부에는 쓰지 않는다.
 * (인라인 이미지만 &lt;img&gt; 가 헤더를 못 실어 예외적으로 쿼리 토큰을 쓴다. MailBodyHtml 주석 참고.)
 *
 * <h3>XHR 방식의 대가 — 그래서 진행률이 필수다</h3>
 * XHR 로 받으면 <b>다 받을 때까지 브라우저 다운로드 표시가 전혀 없다</b>. 파일이 클수록 화면은
 * 아무 일도 안 일어난 것처럼 보인다. 실측(2026-08-14 운영 감사로그 1,000여 건):
 * 4MB 미만 첨부는 사용자당 평균 1.2~1.5회 클릭인데, 5MB 이상은 <b>3.2회</b>로 뛴다.
 * 같은 파일(9.7MB pptx)을 한 사람이 53초 동안 10번 누른 기록도 있다 — 반응이 없으니 계속 누른 것이다.
 * 그 클릭 하나하나가 서버에서 S3 를 다시 통째로 읽는 요청이라, 눌러 볼수록 전체가 더 느려진다.
 * 그래서 이 모듈은 <b>진행률 콜백</b>과 <b>중복 클릭 차단</b>을 호출부에 강제한다.
 */
import { mailApi } from "../api/mailApi";

/**
 * Content-Disposition 에서 파일명 — RFC 5987(filename*) 우선, 없으면 filename="".
 *
 * <p>서버는 NFC 로 정규화한 이름을 퍼센트 인코딩해 두 형식 모두 내려준다(MailAttachmentController).
 * 크로스 오리진(onandme.info → gayasoft.co.kr)에서는 서버가 Access-Control-Expose-Headers 로
 * 열어 줘야만 읽을 수 있고, 못 읽으면 목록 DTO 의 이름으로 떨어진다.
 */
function filenameFrom(headers, fallback) {
    const cd = headers?.["content-disposition"] || "";
    const star = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (star) { try { return decodeURIComponent(star[1]); } catch { /* 인코딩 깨짐 — 폴백 */ } }
    const plain = cd.match(/filename\s*=\s*"([^"]+)"/i);
    if (plain) { try { return decodeURIComponent(plain[1]); } catch { return plain[1]; } }
    return fallback || "attachment";
}

/**
 * 실패 사유를 사람이 읽을 문장으로.
 *
 * <p>responseType:"blob" 이라 <b>에러 본문도 Blob</b> 으로 온다. 그대로 두면 공통
 * {@code getApiErrorMessage} 가 {@code data.message} 를 못 읽고 axios 의 영문 기술 문자열
 * ("Request failed with status code 500")을 그대로 흘린다 — 사용자는 물론 신고를 받는 쪽도
 * 무슨 일이 났는지 알 수 없다. 여기서 Blob 을 풀어 서버가 준 한국어 사유를 꺼낸다.
 */
async function reasonOf(e) {
    const data = e?.response?.data;
    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            const msg = parsed?.message || parsed?.error;
            if (msg) return msg;
        } catch { /* JSON 아님 — 아래 상태코드 분기로 */ }
    }
    if (e?.status === 401) return "세션이 만료되었습니다. 다시 로그인한 뒤 시도해 주세요.";
    if (e?.status === 403) return "이 첨부를 내려받을 권한이 없습니다.";
    if (e?.status === 404) return "첨부 파일을 찾을 수 없습니다. 메일을 다시 열어 확인해 주세요.";
    if (e?.code === "ECONNABORTED") return "다운로드가 시간 안에 끝나지 않았습니다. 잠시 후 다시 시도해 주세요.";
    if (e?.code === "ERR_NETWORK") return "네트워크가 끊겨 다운로드가 중단됐습니다.";
    return "다운로드에 실패했습니다. 잠시 후 다시 시도해 주세요.";
}

/**
 * blob → 사용자 디스크에 저장.
 *
 * <p>objectURL 해제를 <b>미룬다</b>. click() 직후 동기 revoke 는 브라우저가 blob 을 아직 읽기 전에
 * 주소를 없애 저장이 조용히 취소되는 사례가 있다(같은 이유로 AttachmentPreviewModal 이 이미
 * 지연 해제를 쓴다). 파일이 클수록 위험 구간이 길어 여유를 더 둔다.
 */
function saveBlob(blob, name) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * 첨부 1건 내려받아 저장. 성공하면 저장된 파일명을 돌려주고, 실패하면 사람이 읽을 메시지로 throw.
 *
 * @param {number}   attachmentId
 * @param {string}   fallbackName  헤더에서 이름을 못 읽었을 때 쓸 이름 (목록 DTO 의 filename)
 * @param {(pct:number|null)=>void} [onProgress]  0~100. Content-Length 를 못 읽으면 null(불확정)
 */
export async function saveAttachmentToDisk(attachmentId, fallbackName, onProgress) {
    let res;
    try {
        res = await mailApi.downloadAttachment(attachmentId, {
            onDownloadProgress: (evt) => {
                if (!onProgress) return;
                const total = evt?.total;
                onProgress(total ? Math.min(100, Math.round((evt.loaded / total) * 100)) : null);
            },
        });
    } catch (e) {
        throw new Error(await reasonOf(e));
    }
    const blob = res?.data ?? res;
    const name = filenameFrom(res?.headers, fallbackName);
    saveBlob(blob instanceof Blob ? blob : new Blob([blob]), name);
    return name;
}
